import { Cartesian2, Cartesian3 } from "cesium";
import { HALinearSegment } from "./horizontal-alignment-linear-segment";
import { CurveDataType, DEFAULT_SPEED_KMPH, HorizontalAlignmentType, } from "./interface";
import { closestPointOnLine, perpendicularDistanceFromPointToLine, } from "../../../utilities/math-utils";
import { EarthBrainUtils } from "../../../utilities/earthbrainutility";
import { HACircularSegment } from "./horizontal-alignment-circular-segment";
import { generateCompositeAlignmentFromIPs } from "./ip-curve-generator";
/**
 * Transitioned Curve Design
 * This is the most sophisticated type, commonly used for highways and high-speed rail to ensure driver comfort
 * and safety.Technical Name: Spiral-Transitioned Curve or Composite Curve.
 * Components: It consists of a Tangent -> Clothoid (Spiral) -> Circular Arc -> Clothoid (Spiral) -> Tangent.
 * Characteristics: The Clothoid (also known as an Euler spiral) acts as a transition where the radius decreases
 * linearly from infinity to the radius of the circular arc. This allows the driver to turn the steering wheel
 * at a constant rate rather than snapping it into position, and it provides a length over which superelevation
 * (banking) can be gradually introduced.
 */
export class SpiralTransitionedHorizontalAlignment {
    type = HorizontalAlignmentType.SPIRAL_TRANSITIONED;
    // -------------------- Private Instance Variables --------------------
    _rootHACurve;
    _points;
    _length;
    constructor() {
        this._rootHACurve = undefined;
        this._points = [];
        this._length = 0;
    }
    //-------------------- Public Getters/Setters --------------------
    get points() {
        return this._points;
    }
    get segments() {
        return this._rootHACurve?.toArray() || [];
    }
    get length() {
        return this._length;
    }
    //----------------------- Public Methods -----------------------
    dispose() {
        let currentSegment = this._rootHACurve;
        while (currentSegment) {
            const next = currentSegment.next;
            currentSegment.dispose();
            currentSegment = next;
        }
        this._rootHACurve = undefined;
        this._points.length = 0;
        this._length = 0;
    }
    recompute(ipPoints) {
        // let currentSegment = this._rootHACurve;
        // // this will not change the type of segment, it will just recompute the element
        // while (currentSegment) {
        // 	currentSegment.recomputeElement();
        // 	currentSegment = currentSegment.next;
        // }
        // this.postCompute();
        this.dispose();
        this.initializeFromIPPoints(ipPoints);
    }
    initializeFromIPPoints(ipPoints) {
        this.dispose();
        if (ipPoints.length < 2) {
            throw new Error("At least two IP points are required");
        }
        const { curves, newPoints } = generateCompositeAlignmentFromIPs(ipPoints.map((ip) => ({ id: ip.id, point: ip.localPoint })), DEFAULT_SPEED_KMPH);
        const surveyPointsMap = new Map();
        ipPoints.forEach((ip) => surveyPointsMap.set(ip.id, ip));
        newPoints.forEach((p) => surveyPointsMap.set(p.id, {
            id: p.id,
            localPoint: p.point,
        }));
        this.initializeFromCurveData(curves, surveyPointsMap);
        this.postCompute();
        return this._rootHACurve;
    }
    initializeFromCurveData(curveData, surveyPointsMap) {
        this.dispose();
        if (curveData.length === 0) {
            throw new Error("Curve data is empty");
        }
        const getSegment = (data, index) => {
            switch (data.type) {
                case CurveDataType.LINEAR:
                    const startPoint = surveyPointsMap.get(data.startPointId);
                    const endPoint = surveyPointsMap.get(data.endPointId);
                    if (!startPoint || !endPoint) {
                        throw new Error("Survey point not found");
                    }
                    return new HALinearSegment(startPoint, endPoint);
                case CurveDataType.CLOTHOID:
                    // const clothoidStartPoint = surveyPointsMap.get(data.startPointId);
                    // const clothoidEndPoint = surveyPointsMap.get(data.endPointId);
                    // const anchorPoint = surveyPointsMap.get(data.anchorPointId);
                    // if (!clothoidStartPoint || !clothoidEndPoint || !anchorPoint) {
                    // 	throw new Error("Survey point not found");
                    // }
                    // if(data.clothoidType === CLOTHOID_CURVE_TYPE.STRAIGHT_TO_ARC){
                    // const centerPoint = surveyPointsMap.get((curveData[index+1] as IHACircularSegmentData).centerPointId);
                    // if (!centerPoint) {
                    // 	throw new Error("Survey point not found");
                    // }
                    // 	return new HAClothoidSegment(
                    // 	clothoidStartPoint,
                    // 	clothoidEndPoint,
                    // 	data.clothoidType,
                    // 	data.radius,
                    // 	data.parameterA,
                    // 	anchorPoint,
                    // 	centerPoint,
                    // 	data.length,
                    // );
                    // }
                    // else if(data.clothoidType === CLOTHOID_CURVE_TYPE.ARC_TO_STRAIGHT){
                    // 	const centerPoint = surveyPointsMap.get((curveData[index-1] as IHACircularSegmentData).centerPointId);
                    // if (!centerPoint) {
                    // 	throw new Error("Survey point not found");
                    // }
                    // 	return new HAClothoidSegment(
                    // 	clothoidStartPoint,
                    // 	clothoidEndPoint,
                    // 	data.clothoidType,
                    // 	data.radius,
                    // 	data.parameterA,
                    // 	anchorPoint,
                    // 	centerPoint,
                    // 	data.length,
                    // );
                    // }
                    // else{
                    // 	throw new Error("Invalid clothoid type");
                    // }
                    // TODO: implement clothoid segment
                    const clStartPoint = surveyPointsMap.get(data.startPointId);
                    const clEndPoint = surveyPointsMap.get(data.endPointId);
                    if (!clStartPoint || !clEndPoint) {
                        throw new Error("Survey point not found");
                    }
                    return new HALinearSegment(clStartPoint, clEndPoint);
                case CurveDataType.CIRCULAR_SEGMENT:
                    const circularSegmentStartPoint = surveyPointsMap.get(data.startPointId);
                    const circularSegmentEndPoint = surveyPointsMap.get(data.endPointId);
                    const circularSegmentAnchorPoint = surveyPointsMap.get(data.anchorPointId);
                    if (!circularSegmentStartPoint ||
                        !circularSegmentEndPoint ||
                        !circularSegmentAnchorPoint) {
                        throw new Error("Survey point not found");
                    }
                    return new HACircularSegment(circularSegmentStartPoint, circularSegmentEndPoint, circularSegmentAnchorPoint);
                default:
                    throw new Error(`Unknown curve type: ${data.type}`);
            }
        };
        this._rootHACurve = getSegment(curveData[0], 0);
        let currentSegment = this._rootHACurve;
        for (let i = 1; i < curveData.length; i++) {
            const segment = getSegment(curveData[i], i);
            currentSegment.insertAfter(segment);
            currentSegment = segment;
        }
        this.postCompute();
    }
    getClosestPointInfo(point) {
        const getIndexForProbableSegment = (point, pathpoints) => {
            const perpDistances = [];
            for (let i = 1; i < pathpoints.length; i++) {
                const perpDistance = perpendicularDistanceFromPointToLine(point, pathpoints[i - 1], pathpoints[i], true);
                perpDistances.push(perpDistance);
            }
            const indexOfminDistance = perpDistances.indexOf(Math.min(...perpDistances));
            return indexOfminDistance;
        };
        const subSegmentIndex = getIndexForProbableSegment(point, this.points);
        const result = closestPointOnLine(point, this.points[subSegmentIndex], this.points[subSegmentIndex + 1], true);
        let distance = 0;
        for (let i = 0; i < subSegmentIndex; ++i) {
            distance += Cartesian2.distance(this.points[i], this.points[i + 1]);
        }
        distance += Cartesian2.distance(this.points[subSegmentIndex], result);
        return {
            point: result,
            distanceFromStart: distance,
        };
    }
    getInfoAtDistanceInBatch(sortedDistanceFromStart) {
        const distances = [...sortedDistanceFromStart];
        const points = [];
        let cumulativeDistance = 0;
        let prevPoint;
        let pointOnPath;
        for (let i = 0; i < this.points.length; i++) {
            const pathPoint = this.points[i];
            if (prevPoint) {
                const distance = Cartesian2.distance(prevPoint, pathPoint);
                if (distance === 0) {
                    continue;
                }
                cumulativeDistance += distance;
                // multiple distances can be with in two points
                while (distances.length > 0 && cumulativeDistance >= distances[0]) {
                    const direction = EarthBrainUtils.getDirBetweenTwoCartesians(new Cartesian3(prevPoint.x, prevPoint.y, 0), new Cartesian3(pathPoint.x, pathPoint.y, 0));
                    pointOnPath = Cartesian3.add(prevPoint, Cartesian3.multiplyByScalar(direction, distance - cumulativeDistance + distances[0], new Cartesian3()), new Cartesian3());
                    points.push(pointOnPath);
                    distances.shift();
                }
            }
            prevPoint = pathPoint;
        }
        return points;
    }
    getPointInfoAtDistance(distanceFromStart) {
        if (distanceFromStart >= this.length) {
            return Cartesian3.clone(this.points[this.points.length - 1], new Cartesian3());
        }
        let cumulativeDistance = 0;
        let prevPoint;
        let pointOnPath;
        for (let i = 0; i < this.points.length; i++) {
            const pathPoint = this.points[i];
            if (prevPoint) {
                const distance = Cartesian2.distance(prevPoint, pathPoint);
                if (distance === 0) {
                    continue;
                }
                cumulativeDistance += distance;
                if (cumulativeDistance >= distanceFromStart) {
                    const direction = EarthBrainUtils.getDirBetweenTwoCartesians(new Cartesian3(prevPoint.x, prevPoint.y, 0), new Cartesian3(pathPoint.x, pathPoint.y, 0));
                    pointOnPath = Cartesian3.add(prevPoint, Cartesian3.multiplyByScalar(direction, distance - cumulativeDistance + distanceFromStart, new Cartesian3()), new Cartesian3());
                    break;
                }
            }
            prevPoint = pathPoint;
        }
        return pointOnPath;
    }
    getDistanceFromStart(point) {
        let distance = 0;
        const points = this.points;
        for (let i = 0; i < points.length - 1; i++) {
            if (points[i].equals(point)) {
                return distance;
            }
            distance += Cartesian2.distance(points[i], points[i + 1]);
        }
        if (points[points.length - 1].equals(point)) {
            return distance;
        }
        // If point not found exactly, try to find closest point on segments ?
        // For now, assuming point is one of the vertices.
        // If strictly needed for any point on path, we need projection.
        // Given the usage in Alignment.points map, we serve exactly the vertices.
        return -1;
    }
    // -------------------------- Private Methods --------------------------
    computePoints() {
        this._points.length = 0;
        let currentSegment = this._rootHACurve;
        while (currentSegment) {
            this._points.push(...currentSegment.curvePoints);
            currentSegment = currentSegment.next;
        }
    }
    computeLength() {
        this._length = 0;
        for (let i = 0; i < this.points.length - 1; i++) {
            this._length += Cartesian2.distance(this.points[i], this.points[i + 1]);
        }
    }
    postCompute() {
        //post process for linear segments with clothoid on both sides
        //this.postProcess();
        this.computePoints();
        this.computeLength();
    }
    postProcess() {
        let currentSegment = this._rootHACurve;
        while (currentSegment) {
            if (currentSegment.type === CurveDataType.LINEAR) {
                const prevSegment = currentSegment.previous;
                const nextSegment = currentSegment.next;
                if (prevSegment?.type === CurveDataType.CLOTHOID &&
                    nextSegment?.type === CurveDataType.CLOTHOID) {
                    // previous clothoid and next clothoid might cross each other on tangent side
                    // if cross, then we need to make clothoid shorter by cutting off at intersection point which is not the mid point of Linear Segment
                    // since recompute didnt work, we need to manually edit end point and points computed
                    // need to calculate the intersection of previous segment and next segment
                    const dir1 = EarthBrainUtils.getNormalizedDirection(currentSegment.startPoint.localPoint, currentSegment.endPoint.localPoint);
                    const dir2 = EarthBrainUtils.getNormalizedDirection(prevSegment.startPoint.localPoint, nextSegment.endPoint.localPoint);
                    const dot = Cartesian3.dot(dir1, dir2);
                    const midPoint = Cartesian3.midpoint(currentSegment.startPoint.localPoint, currentSegment.endPoint.localPoint, new Cartesian3());
                    if (dot <= 0) {
                        // previous segment
                        // update end point of prev segment to midPoint
                        Cartesian3.clone(midPoint, prevSegment.endPoint.localPoint);
                        // remove points of prev segment after end point
                        //find the parallel points to the tangent direction of prev segment
                        // dot product will grow to max and
                        let dot = -1;
                        for (let i = 0; i < prevSegment.curvePoints.length; i++) {
                            const point = prevSegment.curvePoints[i];
                            if (Cartesian3.equals(point, midPoint)) {
                                const direction = EarthBrainUtils.getNormalizedDirection(point, midPoint); //point and midPoint can be same
                                dot = Cartesian3.dot(direction, dir1);
                            }
                            else {
                                dot = 0;
                            }
                            // dot meaning zero, they are same
                            // if dot becomes less than zero, then remove points after that
                            if (dot < 0) {
                                prevSegment.curvePoints.splice(i - 1, prevSegment.curvePoints.length - i + 1);
                                prevSegment.curvePoints.push(midPoint);
                                break;
                            }
                        }
                        // update start point of next segment to midPoint
                        Cartesian3.clone(midPoint, nextSegment.startPoint.localPoint);
                        // remove points of next segment before start point
                        if (dot <= 0) {
                            // next segment
                            Cartesian3.clone(midPoint, nextSegment.startPoint.localPoint);
                            let dot = -1;
                            const dir1_reversed = Cartesian3.negate(dir1, new Cartesian3());
                            for (let i = 0; i < nextSegment.curvePoints.length; i++) {
                                const point = nextSegment.curvePoints[i];
                                if (!Cartesian3.equals(point, midPoint)) {
                                    const direction = EarthBrainUtils.getNormalizedDirection(point, midPoint); //point and midPoint can be same
                                    dot = Cartesian3.dot(direction, dir1_reversed);
                                }
                                else {
                                    dot = 0;
                                }
                                // dot meaning zero, they are same
                                // if dot becomes less than zero, then remove points after that
                                if (dot < 0) {
                                    nextSegment.curvePoints.splice(0, i);
                                    nextSegment.curvePoints.unshift(midPoint);
                                    break;
                                }
                            }
                        }
                        // remove linear segment
                        currentSegment.dispose();
                        prevSegment.next = nextSegment;
                        nextSegment.previous = prevSegment;
                    }
                }
            }
            currentSegment = currentSegment.next;
        }
    }
}
//# sourceMappingURL=spiral-transitioned-horizontal-alignment.js.map